import React, { useMemo } from 'react';
import type { ReactNode } from 'react';
import { getSafeCountryFromCode, CountryProvider } from '@tectonics/commons/country';
import { getTenantFromCode, TenantProvider } from '@tectonics/commons/tenant';
import { getQueryParamValue } from '@tectonics/utils/url';
import { VolcanesThemeProvider, getThemeById } from '@tectonics/look-and-feel/volcanes/theme';
import { GlobalStyle } from '@tectonics/look-and-feel/volcanes/commons/global-style';
import { EnrollmentProvider, useEnrollment } from './context';
import { useEnrollmentState } from './hooks/use-enrollment-state';
import { useEnrollmentPrefill } from './hooks/use-enrollment-prefill';
import { BankLinkingScreen } from './screens/bank-linking-screen';
import { BankRedirectScreen } from './screens/bank-redirect-screen';
import { PollingAuthScreen } from './screens/polling-auth-screen';
import { SuccessScreen } from './screens/success-screen';
import { ErrorScreen } from './screens/error-screen';
import { PageContainer } from './controller.styles';

interface Props {
  readonly tenantCode: string | null | undefined;
}

export function PixJsrEnrollmentController({ tenantCode }: Props): ReactNode {
  if (!tenantCode) {
    throw new Error('URL query param "tenant" not found');
  }

  const contract = getQueryParamValue('contract');
  if (!contract) {
    throw new Error('URL query param "contract" not found');
  }

  const tenant = getTenantFromCode(tenantCode);
  const country = getSafeCountryFromCode('br');
  const theme = getThemeById('default');

  const step = getQueryParamValue('step');

  return (
    <TenantProvider value={tenant}>
      <CountryProvider value={country}>
        <VolcanesThemeProvider theme={theme}>
          <>
            <GlobalStyle />
            <EnrollmentRoot contract={contract} step={step} />
          </>
        </VolcanesThemeProvider>
      </CountryProvider>
    </TenantProvider>
  );
}

interface EnrollmentRootProps {
  readonly contract: string;
  readonly step: string | null | undefined;
}

function EnrollmentRoot({ contract, step }: EnrollmentRootProps): ReactNode {
  const [data, dispatch] = useEnrollmentState(contract, step);

  const contextValue = useMemo(() => ({ data, dispatch }), [data, dispatch]);

  return (
    <EnrollmentProvider value={contextValue}>
      <PageContainer>
        <EnrollmentScreen />
      </PageContainer>
    </EnrollmentProvider>
  );
}

function EnrollmentScreen(): ReactNode {
  const { data } = useEnrollment();
  const prefill = useEnrollmentPrefill();

  const bankLinkingScreen = <BankLinkingScreen {...prefill} />;

  switch (data.state) {
    case 'BANK_LINKING':
      return bankLinkingScreen;
    case 'INITIATING':
    case 'BANK_REDIRECT':
      return <BankRedirectScreen />;
    case 'POLLING':
    case 'READY_TO_AUTH':
    case 'AUTHENTICATING':
    case 'SUBMITTING_AUTH':
      return <PollingAuthScreen />;
    case 'SUCCESS':
      return <SuccessScreen />;
    case 'ERROR':
      return <ErrorScreen />;
    default:
      return bankLinkingScreen;
  }
}
